import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Dropdown, Form, Row, Table } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExport, faSync } from '@fortawesome/free-solid-svg-icons';
import { useCookies } from 'react-cookie';
import { getCsvReward, getListReward } from '../../../../controller/api/statistic/reward';
import { calculatePage } from '../../../../helper/helper';
import Pagination from '../../../../components/Pagination';
import ExportProcessingPopup from '../../../../components/ExportProcessingPopup';
import { Context } from '../Reward';
import { TableRow } from '../component/TableRow';
import GameQuickFilter from '../component/GameQuickFilter';
import { defaultRewardColumns, loadRewardColumns, rewardColumns, sortableRewardColumns } from '../rewardView';
import RewardDetail from '../sub-screen/reward-detail/RewardDetail';
import PayReward from '../sub-screen/pay-reward/PayReward';
import CheckTicket from '../sub-screen/check-ticket/CheckTicket';
import PayRewardAll from '../sub-screen/pay-reward-all/PayRewardAll';
import BigPayReward from '../../big-reward/sub-screen/pay-reward/PayReward';
import ImagePreview from '../../../__classic__/order/partials/image-preview/ImagePreview';

export default function RewardTable() {
  const { state, dispatch } = useContext(Context);
  const [cookies] = useCookies(['lottmekong']);
  const [loading, setLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [checkTicketModalShow, setCheckTicketModalShow] = useState(false);
  const [payModalShow, setPayModalShow] = useState(false);
  const [bigPayModalShow, setBigPayModalShow] = useState(false);
  const [payAllModalShow, setPayAllModalShow] = useState(false);
  const [imageModalShow, setImageModalShow] = useState(false);
  const latestRequest = useRef(0);
  const rewardRef = useRef({});
  const transIdRef = useRef('');
  const userIdRef = useRef('');
  const ticketIdRef = useRef('');
  const gamePeriodIdRef = useRef('');
  const phoneRef = useRef('');
  const selectedImageRef = useRef('');
  const imageTitleRef = useRef('');
  const [columnSettings, setColumnSettings] = useState(() => ({
    scope: state.filter.gameScope,
    columns: loadRewardColumns(state.filter.gameScope)
  }));
  const visibleColumns = columnSettings.scope === state.filter.gameScope
    ? columnSettings.columns
    : loadRewardColumns(state.filter.gameScope);

  const { from, to, phone, gameId, gameScope, paymentStatus, minReward, maxReward, page, sortBy, sortDirection } = state.filter;

  const setColumns = columns => {
    setColumnSettings({ scope: gameScope, columns });
    try {
      localStorage.setItem(`reward-columns:${gameScope}`, JSON.stringify(columns));
    } catch (_) {
      // Storage may be unavailable.
    }
  };

  const load = useCallback(async () => {
    void state.filterPressCount;
    const requestId = ++latestRequest.current;
    setLoading(true);
    try {
      const response = await getListReward(cookies.lottmekong, {
        from, to, phone, gameId, gameScope, paymentStatus, minReward, maxReward, sortBy, sortDirection
      }, page);
      if (requestId !== latestRequest.current) return;
      dispatch({
        type: 'UPDATE_DATA',
        payload: {
          list: response?.data || [],
          total: response?.total || 0,
          totalPage: response?.total ? calculatePage(response.total) : 0
        }
      });
    } catch (error) {
      console.error('Error fetching reward data:', error);
    } finally {
      if (requestId === latestRequest.current) {
        setLoading(false);
        dispatch({ type: 'SET_FILTER_LOADING', payload: false });
      }
    }
  }, [cookies.lottmekong, from, to, phone, gameId, gameScope, paymentStatus, minReward, maxReward, page, sortBy, sortDirection, state.filterPressCount, dispatch]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setColumnSettings({ scope: gameScope, columns: loadRewardColumns(gameScope) }); }, [gameScope]);
  useEffect(() => { setPayAllModalShow(state.isPaying); }, [state.isPaying]);

  const fetchCsvPage = useCallback((csvPage, dates) => getCsvReward(cookies.lottmekong, {
    from: dates.from,
    to: dates.to,
    phone,
    gameId,
    gameScope,
    paymentStatus,
    minReward,
    maxReward,
    sortBy,
    sortDirection
  }, csvPage), [cookies.lottmekong, phone, gameId, gameScope, paymentStatus, minReward, maxReward, sortBy, sortDirection]);

  const doSort = column => {
    const backendColumn = column === 'status' ? 'isPaid' : column;
    dispatch({
      type: 'SUBMIT_FILTER',
      payload: {
        sortBy: backendColumn,
        sortDirection: sortBy === backendColumn && sortDirection === 'asc' ? 'desc' : 'asc'
      }
    });
  };

  const onShowDetailPress = reward => { rewardRef.current = reward; setModalShow(true); };
  const onShowCheckTicketPress = (ticketId, periodId) => {
    ticketIdRef.current = ticketId;
    gamePeriodIdRef.current = periodId;
    setCheckTicketModalShow(true);
  };
  const onPreviewImage = (title, image) => {
    imageTitleRef.current = title;
    selectedImageRef.current = image;
    setImageModalShow(true);
  };
  const onPaidPress = reward => {
    userIdRef.current = reward.user.id;
    phoneRef.current = reward.user.phone;
    transIdRef.current = reward.id;
    ticketIdRef.current = reward.ticket.id;
    if (Number(reward.grossValue) > 10000000) setBigPayModalShow(true);
    else setPayModalShow(true);
  };

  const markPaidAndRefresh = () => {
    const newData = JSON.parse(JSON.stringify(state.data));
    const reward = newData.list.find(item => item.id === transIdRef.current);
    if (reward) reward.isPaid = true;
    dispatch({ type: 'UPDATE_DATA', payload: newData });
    dispatch({ type: 'REFRESH' });
  };

  return (
    <>
      <GameQuickFilter loading={loading || state.isFilterLoading} />
      <Card border="light" className="shadow-sm">
        <Card.Header className="bg-white px-3 py-2">
          <Row className="align-items-center">
            <Col>
              <h6 className="mb-0">Danh sách trúng thưởng</h6>
              <small className="text-muted">{Number(state.data.total || 0).toLocaleString('vi-VN')} vé trúng</small>
            </Col>
            <Col xs="auto">
              <div className="d-flex flex-wrap gap-2">
                <Button size="sm" variant="outline-primary" disabled={loading} onClick={() => dispatch({ type: 'REFRESH' })}>
                  <FontAwesomeIcon icon={faSync} spin={loading} /> Làm mới
                </Button>
                <Button size="sm" variant="success" onClick={() => setShowExportModal(true)}>
                  <FontAwesomeIcon icon={faFileExport} /> Xuất file
                </Button>
                <Button size="sm" variant="outline-danger" onClick={() => dispatch({ type: 'PAY_REWARD_ALL' })}>Trả thưởng</Button>
                <Dropdown alignRight>
                  <Dropdown.Toggle size="sm" variant="outline-primary">Tùy chỉnh cột</Dropdown.Toggle>
                  <Dropdown.Menu className="reward-columns-menu">
                    <strong className="d-block mb-2">Tùy chỉnh cột</strong>
                    {rewardColumns.map(([id, label]) => (
                      <Form.Check
                        key={id}
                        id={`reward-column-${id}`}
                        className="mb-2"
                        label={id === 'actions' ? `${label} (bắt buộc)` : label}
                        checked={visibleColumns.includes(id)}
                        disabled={id === 'actions'}
                        onChange={event => setColumns(event.target.checked ? [...visibleColumns, id] : visibleColumns.filter(column => column !== id))}
                      />
                    ))}
                    <Button size="sm" variant="link" onClick={() => setColumns([...defaultRewardColumns])}>Khôi phục mặc định</Button>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </Col>
          </Row>
        </Card.Header>
        <Table responsive className="reward-table align-items-center table-bordered table-hover">
          <thead className="text-center">
            <tr>
              {rewardColumns.filter(([id]) => visibleColumns.includes(id)).map(([id, label]) => {
                const backendColumn = id === 'status' ? 'isPaid' : id;
                return (
                  <th key={id}>
                    {sortableRewardColumns.includes(id)
                      ? <button type="button" className="reward-sort-button" onClick={() => doSort(id)}>{label} {sortBy === backendColumn ? sortDirection === 'asc' ? '↑' : '↓' : '↕'}</button>
                      : label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && !state.data.list?.length
              ? <tr><td colSpan={visibleColumns.length} className="text-center py-4"><FontAwesomeIcon icon={faSync} spin /> Đang tải dữ liệu...</td></tr>
              : !state.data.list?.length
                ? <tr><td colSpan={visibleColumns.length} className="text-center py-4 text-muted">Không có vé trúng thưởng nào</td></tr>
                : state.data.list.map(item => (
                  <TableRow
                    key={item.id}
                    data={item}
                    visibleColumns={visibleColumns}
                    onCheckPress={onShowCheckTicketPress}
                    onPaidPress={onPaidPress}
                    onShowDetailPress={onShowDetailPress}
                    onPreviewImage={onPreviewImage}
                  />
                ))}
          </tbody>
        </Table>
        <Card.Footer>
          <Pagination
            totalPage={state.data.totalPage || 0}
            currentPage={page || 0}
            onPageChange={nextPage => dispatch({ type: 'UPDATE_PAGE', payload: nextPage })}
          />
        </Card.Footer>
      </Card>

      <ExportProcessingPopup show={showExportModal} defaultFrom={from} defaultTo={to} fetchPage={fetchCsvPage} onHide={() => setShowExportModal(false)} />
      <RewardDetail show={modalShow} reward={rewardRef.current} onHide={() => setModalShow(false)} />
      <PayReward
        show={payModalShow}
        transId={transIdRef.current}
        userId={userIdRef.current}
        ticketId={ticketIdRef.current}
        onHide={status => {
          if (status === 1) markPaidAndRefresh();
          setPayModalShow(false);
        }}
      />
      <BigPayReward
        show={bigPayModalShow}
        transId={transIdRef.current}
        ticketId={ticketIdRef.current}
        phone={phoneRef.current}
        onHide={status => {
          if (status === 1) markPaidAndRefresh();
          setBigPayModalShow(false);
        }}
      />
      <PayRewardAll
        show={payAllModalShow}
        onHide={status => {
          if (status === 1) dispatch({ type: 'PAY_REWARD_ALL' });
        }}
      />
      <CheckTicket
        show={checkTicketModalShow}
        ticketId={ticketIdRef.current}
        periodId={gamePeriodIdRef.current}
        onHide={status => {
          if (status === 1) window.location.reload();
          setCheckTicketModalShow(false);
        }}
      />
      <ImagePreview show={imageModalShow} title={imageTitleRef.current} image={selectedImageRef.current} onHide={() => setImageModalShow(false)} />
    </>
  );
}
